/**
 * AddressMapPicker — Selector de dirección con marcador arrastrable en el mapa.
 *
 * Permite: (1) escribir la dirección y geocodificar (OSM Nominatim), (2) click
 * en el mapa para colocar el marcador GPS, (3) arrastrar el marcador para
 * ajustar posición fina, (4) usar geolocalización del dispositivo.
 *
 * Props:
 *   - address, onAddressChange
 *   - lat, lng, onLocationChange({lat, lng})
 *   - zoneGeometry (opcional) — dibuja el polígono/círculo de la zona seleccionada
 *   - height (default 300)
 */
import { useState, useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, Polygon, Circle, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Locate, Search, Loader2, Check } from "lucide-react";
import FloatingPanel from "./ui/FloatingPanel";   // v101.98.0 · sugerencias fuera de modales / tarjetas

// Fix Leaflet default icon (missing marker sprite in webpack)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Icono destacado corporativo
const PIN_ICON = L.divIcon({
  className: "",
  html: `<div style="background:#DC2626;color:#fff;width:32px;height:32px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:14px;border:3px solid #fff;box-shadow:0 3px 8px rgba(0,0,0,.4);"><span style="transform:rotate(45deg)">📍</span></div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

const QUITO = [-0.1807, -78.4678];

function CenterMap({ position }) {
  const map = useMap();
  useEffect(() => {
    if (position && position[0] && position[1]) {
      map.setView(position, 16, { animate: true });
    }
  }, [position, map]);
  return null;
}

function ClickHandler({ onClick }) {
  useMapEvents({
    click(e) { onClick(e.latlng); },
  });
  return null;
}

export default function AddressMapPicker({
  address,
  onAddressChange,
  lat,
  lng,
  onLocationChange,
  zoneGeometry = null,
  zoneType = null,
  height = 300,
  disabled = false,
}) {
  const [searching, setSearching] = useState(false);
  const [locating, setLocating] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceRef = useRef(null);
  const suggestAnchorRef = useRef(null);   // v101.98.0

  const hasCoords = lat != null && lng != null && lat !== "" && lng !== "";
  const position = hasCoords ? [Number(lat), Number(lng)] : null;

  // Geocode dirección a coordenadas (debounced)
  useEffect(() => {
    if (!address || address.trim().length < 4) {
      setSuggestions([]);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const q = encodeURIComponent(address + ", Ecuador");
        const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${q}&format=json&limit=5&countrycodes=ec&addressdetails=1`);
        const data = await res.json();
        setSuggestions(data || []);
      } catch (e) {
        console.error("Nominatim error:", e);
      } finally {
        setSearching(false);
      }
    }, 700);
    return () => clearTimeout(debounceRef.current);
  }, [address]);

  const pickSuggestion = (s) => {
    onLocationChange?.({ lat: Number(s.lat), lng: Number(s.lon) });
    onAddressChange?.(s.display_name);
    setShowSuggestions(false);
  };

  const handleMapClick = (latlng) => {
    if (disabled) return;
    onLocationChange?.({ lat: latlng.lat, lng: latlng.lng });
  };

  const handleMarkerDrag = (e) => {
    const { lat: newLat, lng: newLng } = e.target.getLatLng();
    onLocationChange?.({ lat: newLat, lng: newLng });
  };

  const geolocate = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        onLocationChange?.({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocating(false);
      },
      (err) => {
        console.error(err);
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  return (
    <div className="space-y-2" data-testid="address-map-picker">
      {/* Input + Autocomplete */}
      <div className="relative" ref={suggestAnchorRef}>
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              type="text"
              value={address || ""}
              onChange={(e) => onAddressChange?.(e.target.value)}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              placeholder="Ej: Av. 6 de Diciembre N32-12, Quito"
              disabled={disabled}
              className="w-full pl-9 pr-9 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition disabled:bg-slate-100"
              data-testid="picker-address-input"
            />
            {searching && (
              <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-600 animate-spin" />
            )}
          </div>
          <button
            type="button"
            onClick={geolocate}
            disabled={locating || disabled}
            className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs rounded-md border border-slate-300 transition disabled:opacity-40"
            title="Usar mi ubicación actual"
            data-testid="picker-geolocate-btn"
          >
            {locating ? <Loader2 size={12} className="animate-spin" /> : <Locate size={12} />}
            <span className="hidden sm:inline">Mi ubicación</span>
          </button>
        </div>

        {/* Sugerencias autocomplete */}
        {showSuggestions && suggestions.length > 0 && (
          <FloatingPanel anchorRef={suggestAnchorRef} matchWidth maxHeight={224} className="bg-white border border-slate-200 rounded-md shadow-lg" data-testid="picker-suggestions">
            {suggestions.map((s, i) => (
              <button
                key={i}
                type="button"
                onMouseDown={() => pickSuggestion(s)}
                className="w-full text-left px-3 py-2 text-xs hover:bg-emerald-50 border-b border-slate-100 last:border-0 flex items-start gap-2"
                data-testid={`picker-suggestion-${i}`}
              >
                <MapPin size={12} className="text-emerald-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="truncate text-slate-900">{s.display_name}</div>
                  <div className="text-[10px] text-slate-500 font-mono">
                    {Number(s.lat).toFixed(6)}, {Number(s.lon).toFixed(6)}
                  </div>
                </div>
              </button>
            ))}
          </FloatingPanel>
        )}
      </div>

      {/* Mapa con marcador */}
      <div className="rounded-md overflow-hidden border border-slate-200 relative" style={{ height }}>
        <MapContainer center={position || QUITO} zoom={position ? 16 : 11} style={{ height: "100%", width: "100%" }}>
          <TileLayer
            attribution='&copy; OpenStreetMap'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <CenterMap position={position} />
          <ClickHandler onClick={handleMapClick} />

          {/* Polígono/círculo de la zona operativa (referencia visual) */}
          {zoneType === "polygon" && Array.isArray(zoneGeometry?.coordinates) && zoneGeometry.coordinates.length >= 3 && (
            <Polygon
              positions={zoneGeometry.coordinates.map((c) => [c.lat || c[0], c.lng || c[1]])}
              pathOptions={{ color: "#059669", weight: 2, fillOpacity: 0.1, dashArray: "5,5" }}
            />
          )}
          {zoneType === "circle" && zoneGeometry?.center && (
            <Circle
              center={[zoneGeometry.center.lat || zoneGeometry.center[0], zoneGeometry.center.lng || zoneGeometry.center[1]]}
              radius={(zoneGeometry.radius_km || 2.5) * 1000}
              pathOptions={{ color: "#059669", weight: 2, fillOpacity: 0.1, dashArray: "5,5" }}
            />
          )}

          {/* Marcador arrastrable */}
          {position && (
            <Marker
              position={position}
              icon={PIN_ICON}
              draggable={!disabled}
              eventHandlers={{ dragend: handleMarkerDrag }}
            />
          )}
        </MapContainer>

        {/* Instrucciones flotantes */}
        {!position && (
          <div className="absolute top-2 left-1/2 -translate-x-1/2 z-[400] bg-white/95 backdrop-blur px-3 py-1.5 rounded-md shadow border border-slate-200 text-[11px] text-slate-700 flex items-center gap-1.5">
            <MapPin size={11} className="text-red-600" />
            <span>Click en el mapa o busca la dirección arriba para marcar el punto</span>
          </div>
        )}
      </div>

      {/* Coords + hint */}
      <div className="flex items-center justify-between text-[10px] text-slate-600">
        <div className="flex items-center gap-3">
          {position ? (
            <span className="inline-flex items-center gap-1 text-emerald-700 font-mono">
              <Check size={11} /> GPS: <b>{position[0].toFixed(6)}, {position[1].toFixed(6)}</b>
            </span>
          ) : (
            <span className="text-slate-500">Sin coordenadas. Marca un punto en el mapa.</span>
          )}
        </div>
        <div className="text-slate-500 hidden sm:block">
          Click en mapa · Arrastra pin · Doble-click zoom
        </div>
      </div>
    </div>
  );
}
